import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import type { 
  Payment, 
  PaymentRequest, 
  PaymentResponse,
  PaymentStatus,
  PaymentProvider 
} from '../types/payment';

export const usePayments = (organizationId?: string) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setPayments([]);
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const fetchPayments = async () => {
      try {
        // Simple query without orderBy to avoid index requirement
        const q = query(
          collection(db, 'payments'),
          where('organizationId', '==', organizationId)
        );

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const paymentsData = snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                paidAt: data.paidAt?.toDate(),
                cancelledAt: data.cancelledAt?.toDate(),
                refundedAt: data.refundedAt?.toDate(),
                webhookReceivedAt: data.webhookReceivedAt?.toDate()
              } as Payment;
            });
            
            // Sort in memory instead of in query
            paymentsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
            
            setPayments(paymentsData);
            setLoading(false);
          },
          (err) => {
            console.error('Error fetching payments:', err);
            setError(err.message);
            setLoading(false);
          }
        );
      } catch (err: any) {
        console.error('Error setting up payments listener:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPayments();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [organizationId]);

  /**
   * Create a MyPOS payment (OAuth 2.0 API - 2025)
   */
  const createMyPosPayment = async (
    credentialId: string,
    paymentRequest: PaymentRequest
  ): Promise<PaymentResponse> => {
    try {
      // 1. Create payment record in Firestore
      const paymentData: any = {
        orderId: paymentRequest.orderId,
        purpose: paymentRequest.purpose,
        provider: 'mypos' as PaymentProvider,
        status: 'pending' as PaymentStatus,
        amount: paymentRequest.amount,
        currency: paymentRequest.currency,
        customerName: paymentRequest.customerName,
        organizationId: paymentRequest.organizationId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Only add optional fields if they have values (Firestore doesn't allow undefined)
      if (paymentRequest.customerEmail) paymentData.customerEmail = paymentRequest.customerEmail;
      if (paymentRequest.customerPhone) paymentData.customerPhone = paymentRequest.customerPhone;
      if (paymentRequest.customerId) paymentData.customerId = paymentRequest.customerId;
      if (paymentRequest.invoiceId) paymentData.invoiceId = paymentRequest.invoiceId;
      if (paymentRequest.ticketId) paymentData.ticketId = paymentRequest.ticketId;
      if (paymentRequest.description) paymentData.description = paymentRequest.description;
      if (paymentRequest.note) paymentData.note = paymentRequest.note;
      if (paymentRequest.metadata) paymentData.metadata = paymentRequest.metadata;

      const paymentDoc = await addDoc(collection(db, 'payments'), paymentData);

      // 2. Prepare customer name
      const nameParts = paymentRequest.customerName.trim().split(' ');
      const customerFirstName = nameParts[0] || '';
      const customerLastName = nameParts.slice(1).join(' ') || '';

      // 3. Call MyPOS IPC Purchase API (Legacy - but working with test credentials)
      const myposCreatePurchase = httpsCallable(functions, 'myposCreatePurchase');
      const result = await myposCreatePurchase({
        credentialId,
        amount: paymentRequest.amount,
        currency: paymentRequest.currency,
        orderID: paymentRequest.orderId,
        customerEmail: paymentRequest.customerEmail || '',
        customerPhone: paymentRequest.customerPhone || '',
        customerFirstName,
        customerLastName,
        note: paymentRequest.note || paymentRequest.description || '',
        urlOk: paymentRequest.okUrl,
        urlCancel: paymentRequest.cancelUrl,
        urlNotify: paymentRequest.notifyUrl,
        cartItems: paymentRequest.metadata?.cartItems || []
      });

      const response = result.data as any;

      // 4. Update payment record and submit form to MyPOS
      if (response.success && response.data) {
        const formData = response.data.formData;
        const formUrl = response.data.formUrl;
        
        if (!formData || !formUrl) {
          throw new Error('No form data received from MyPOS');
        }

        const updateData: any = {
          status: 'pending' as PaymentStatus,  // Pending until user completes payment
          updatedAt: serverTimestamp()
        };

        // Only add fields if they exist
        if (response.data.IPC_Trnref || response.data.ipc_trnref) {
          updateData.providerPaymentId = response.data.IPC_Trnref || response.data.ipc_trnref;
        }

        await updateDoc(doc(db, 'payments', paymentDoc.id), updateData);

        // Create and submit HTML form to MyPOS checkout
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = formUrl;
        form.style.display = 'none';

        // Add all form fields
        Object.keys(formData).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = formData[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();

        return {
          success: true,
          paymentId: paymentDoc.id,
          paymentUrl: formUrl,
          provider: 'mypos'
        };
      } else {
        throw new Error(response.message || 'Failed to create MyPOS payment');
      }
    } catch (error: any) {
      console.error('Error creating MyPOS payment:', error);
      throw new Error(error.message || 'Failed to create payment');
    }
  };

  /**
   * Get payment status from MyPOS
   */
  const getPaymentStatus = async (
    credentialId: string,
    orderID: string
  ): Promise<any> => {
    try {
      const myposGetPaymentStatus = httpsCallable(functions, 'myposGetPaymentStatus');
      const result = await myposGetPaymentStatus({
        credentialId,
        orderID
      });

      return result.data;
    } catch (error: any) {
      console.error('Error getting payment status:', error);
      throw new Error(error.message || 'Failed to get payment status');
    }
  };

  /**
   * Update payment status (typically called from webhook)
   */
  const updatePaymentStatus = async (
    paymentId: string,
    status: PaymentStatus,
    webhookData?: any
  ): Promise<void> => {
    try {
      const updateData: any = {
        status,
        updatedAt: serverTimestamp()
      };

      if (webhookData) {
        updateData.webhookData = webhookData;
        updateData.webhookReceivedAt = serverTimestamp();
      }

      if (status === 'completed') {
        updateData.paidAt = serverTimestamp();
      } else if (status === 'cancelled') {
        updateData.cancelledAt = serverTimestamp();
      } else if (status === 'refunded' || status === 'partial_refund') {
        updateData.refundedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'payments', paymentId), updateData);
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      throw new Error(error.message || 'Failed to update payment status');
    }
  };

  /**
   * Refund a payment
   */
  const refundPayment = async (
    credentialId: string,
    paymentId: string,
    amount?: number
  ): Promise<void> => {
    try {
      const payment = payments.find(p => p.id === paymentId);
      if (!payment) {
        throw new Error('Payment not found');
      }

      const myposRefund = httpsCallable(functions, 'myposRefund');
      const result = await myposRefund({
        credentialId,
        ipcTranID: payment.providerPaymentId,
        amount: amount || payment.amount
      });

      const response = result.data as any;

      if (response.success) {
        await updatePaymentStatus(
          paymentId,
          amount && amount < payment.amount ? 'partial_refund' : 'refunded',
          response.data
        );
      } else {
        throw new Error(response.message || 'Refund failed');
      }
    } catch (error: any) {
      console.error('Error refunding payment:', error);
      throw new Error(error.message || 'Failed to refund payment');
    }
  };

  return {
    payments,
    loading,
    error,
    createMyPosPayment,
    getPaymentStatus,
    updatePaymentStatus,
    refundPayment
  };
};
